import React, { useState } from 'react';
import styled from 'styled-components';
import { StaticImage } from 'gatsby-plugin-image';
import { StringParam, useQueryParam } from 'use-query-params';
import produce from 'immer';
import {
  Experience,
  ExperienceInfoResponse,
  ExperienceStatus,
  Schedule,
} from '@/webapi/use-experience-api';
import { HSpace, VSpace } from '@/components/spacing';
import { Heading1 } from '@/components/typography';
import {
  AudiencePreview,
  ExpStatusChip,
} from '@/features/details/experiment-details-audience-preview';
import { DescriptionTooltip } from '@/components/tooltip';
import { Flexbox } from '@/components/flex';
import { Chip } from '@/features/details/shared';
import { PreviewChip } from '@/features/details/preview-chip';
import { ReBalanceChip } from '@/features/details/rebalance-chip/allocation-chip';
import { DatePicker } from '@/features/details/date-pick';
import { SessionTypeChip } from '@/features/details/sessions-type-chip';
import { AudiencesChip } from '@/features/details/audiences-chip';
import { DownloadOrdersChip } from '@/features/details/download-orders-chip';
import { DotLabel, getChipColor } from '@/components/dot-label';
import { parseScheduledTime } from '@/utils/experience-utils';
import { formatToLocalDate } from '@/features/details/utils';
import { nav } from '@/utils/browser';
import { editorRoute, Pages } from '@/webapi/pages';
import { useFeatureBit } from '@/features/account-context';
import { FeatureBit } from '@/webapi/use-auth-api';
import { ExperienceInfoShortLine } from '@/features/shared/experience-info-short-line';
import { LabelsStrip } from '@/features/shared/labels-strip';
import { breakpoints, isMobile } from '@/components/responsive';

export function ControlsStrip({
  currentExperienceStatus,
  experience,
  setExperience,
  showPauseModal,
  showArchiveModal,
  pauseRef,
  archiveRef,
}: {
  currentExperienceStatus: ExperienceStatus;
  experience: Experience;
  setExperience: (e: Experience) => void;
  showArchiveModal: () => void;
  showPauseModal: () => void;
  pauseRef: React.MutableRefObject<any>;
  archiveRef: React.MutableRefObject<any>;
}) {
  const {
    id,
    schedule,
    updatedAt: lastEditAt,
    updatedBy: lastEditBy,
    metricsStartAt,
    publishedAt,
    archivedAt,
    endDate,
    versions,
    isPostPurchase,
    status,
    publishedTargeting: targeting,
    isHidden,
  } = experience;
  const { name, description } = experience;

  const onEdit = async () => {
    await nav(editorRoute(experience.id));
  };
  const audienceEnabled = useFeatureBit(
    FeatureBit.SHOW_AUDIENCE_ON_REPORTS_PAGE,
  );

  const onEnd = async () => {
    if (experience.status === ExperienceStatus.PAUSED) {
      showArchiveModal();
    } else if (experience.status === ExperienceStatus.PUBLISHED) {
      showPauseModal();
    }
  };

  const [isPickerVisible, setIsPickerVisible] = useState(false);
  const onDatePickerClicked = () => {
    setIsPickerVisible(!isPickerVisible);
  };
  const [from] = useQueryParam(`from`, StringParam);

  const isMobileDevice = isMobile();

  const bg = getChipColor(currentExperienceStatus);
  if (isHidden) {
    return (
      <AudienceHeader
        name={name}
        description={description}
        id={id}
        isPickerVisible={isPickerVisible}
        onDatePickerClicked={onDatePickerClicked}
      />
    );
  }

  const onExperienceInfoChanged = (info: ExperienceInfoResponse) => {
    setExperience(
      produce(experience, (draft: Experience) => {
        draft.name = info.name;
        draft.description = info.description;
        draft.beforeImages = info.beforeImages;
        draft.afterImages = info.afterImages;
        draft.labels = info.labels
          ?.filter((l) => l.selected)
          ?.map((l) => l.label);
      }),
    );
  };

  const backToReports = from === `reports`;
  return (
    <Controls>
      <BackToLive
        onClick={() =>
          backToReports
            ? nav(`${Pages.DASHBOARD}?pg=REPORTS&scrollTo=uplifts`)
            : nav(Pages.DASHBOARD)
        }
      >{`< Back to ${
        backToReports ? `reports` : `all live experiences`
      } `}</BackToLive>
      <VSpace value={1.5} />
      <Heading1 id="details-heading">
        {name}
        {audienceEnabled && <HSpace className="only-desktop" />}
        {audienceEnabled && (
          <ExpStatusChip
            className="only-desktop"
            bg={bg}
            currentExperienceStatus={currentExperienceStatus}
          />
        )}
      </Heading1>
      <InfoWrapper>
        {experience?.labels?.length > 0 && (
          <>
            <VSpace value={1.5} />
            <LabelsStrip labels={experience.labels} />
          </>
        )}
        <VSpace className="only-desktop" value={1.8} />
        <ExperienceInfoShortLine
          maxWidth="80rem"
          onExperienceInfoChanged={onExperienceInfoChanged}
          currentExperience={experience}
          nonePlaceholder="+ Add more info"
        />
        <VSpace value={1.5} />
      </InfoWrapper>
      <Flexbox
        direction="row"
        justify="center"
        align="center"
        mbJustify="flex-start"
        mbOverflow="scroll"
        mbPadding="0 2rem"
        className="more-info-box mask"
      >
        {currentExperienceStatus === ExperienceStatus.SCHEDULED ? (
          <ScheduledDateChip
            schedule={schedule}
            status={currentExperienceStatus}
            lastEditAt={lastEditAt}
          />
        ) : (
          <StartDateChip
            metricsStartAt={metricsStartAt}
            status={currentExperienceStatus}
            publishedAt={publishedAt}
            lastEditAt={lastEditAt}
            lastEditBy={lastEditBy}
            endDate={archivedAt || endDate}
          />
        )}
        {audienceEnabled && <AudiencePreview targeting={targeting} />}
      </Flexbox>
      <VSpace value={2} />
      <ControlsLayout ref={pauseRef} className="mask">
        {!isMobileDevice && !archivedAt && (
          <Chip onClick={onEdit}>
            <EditIcon />
            Edit
          </Chip>
        )}
        {!archivedAt && (
          <Chip ref={archiveRef} onClick={onEnd}>
            <EndIcon />
            {status === ExperienceStatus.PAUSED ? `Archive` : `Pause`}
          </Chip>
        )}
        {!isPostPurchase && <PreviewChip experience={experience} />}
        <ReBalanceChip experience={experience} setExperience={setExperience} />
        <DatePicker open={isPickerVisible} onClick={onDatePickerClicked} />
        {!isPostPurchase && (
          <SessionTypeChip expId={id} metricsStart={metricsStartAt} />
        )}
        {!isPostPurchase && <AudiencesChip experience={experience} />}
        {!isPostPurchase && !isMobileDevice && (
          <DownloadOrdersChip
            expId={id}
            metricsStart={metricsStartAt}
            versions={versions}
            experience={experience}
          />
        )}
      </ControlsLayout>
      <VSpace value={2.5} />
    </Controls>
  );
}

const DescrWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  :hover {
    color: #4f5359;
  }

  :active {
    color: #89959c;
  }
`;
const Descr = styled.p`
  margin-top: 2rem;
  color: black;
  font-size: 1.2rem;
  font-family: Inter, serif;
  font-weight: 400;

  width: 600px;
  text-overflow: ellipsis;
  overflow-x: hidden;
  white-space: nowrap;
`;
const BackToLive = styled.a`
  text-decoration: none;
  font-size: 1.2rem;
  font-weight: 500;
  color: #89959c;
  font-family: Inter, serif;
  cursor: pointer;
  transition: color 0.3s linear;

  :hover {
    color: #4f5359;
  }

  :active {
    color: #89959c;
  }
`;

function EditIcon() {
  return (
    <>
      <StaticImage
        src="../../assets/edit.svg"
        alt="edit"
        placeholder="none"
        loading="eager"
        height={10}
      />
      <HSpace value={0.5} />
    </>
  );
}

function EndIcon() {
  return (
    <>
      <StaticImage
        src="../../assets/stop.svg"
        alt="end"
        placeholder="none"
        loading="eager"
        height={10}
      />
      <HSpace value={0.5} />
    </>
  );
}

const Controls = styled.div`
  margin: 14px auto 0 auto;
  text-align: center;
  max-width: 1280px;

  #details-heading {
    text-align: center;
    max-width: 90%;
    margin: 0 auto;
  }

  ${breakpoints.down(`md`)} {
    max-width: 100vw;

    .more-info-box > * {
      flex: 0 0 auto;
    }

    .mask {
      mask-image: linear-gradient(
        var(--mask-direction, to right),
        hsl(0 0% 0% / 0),
        rgb(248 249 251 / 1) 10%,
        rgb(248 249 251 / 1) 90%,
        hsl(0 0% 0% / 0)
      );
    }
  }
`;

const ControlsLayout = styled(Flexbox)`
  && {
    position: relative;
    flex-direction: row;
    justify-content: center;

    gap: 1rem;

    ${breakpoints.down(`md`)} {
      justify-content: flex-start;
      overflow-x: scroll;
      padding: 0 2rem;

      && > * {
        flex: 0 0 auto;
      }
    }
  }
`;

function StartDateChip({
  metricsStartAt,
  publishedAt,
  lastEditAt,
  status,
  endDate,
  lastEditBy,
}: {
  metricsStartAt: string | undefined;
  publishedAt: string | undefined;
  lastEditAt: string | undefined;
  status: ExperienceStatus;
  endDate: string | Date | undefined;
  lastEditBy: string | undefined;
}) {
  let lastEdit = ` (Last edit ${formatToLocalDate(lastEditAt)}${
    lastEditBy ? ` by ${lastEditBy}` : ``
  })`;
  if (status === ExperienceStatus.ARCHIVED && endDate) {
    lastEdit = ` (End date ${formatToLocalDate(endDate)})`;
  }

  return (
    <DotLabel dotColor={getChipColor(status)} textColor="#6b6b6b" noDot>
      <StartDateText>
        {`Start date: ${formatToLocalDate(metricsStartAt || publishedAt)}${
          lastEditAt ? lastEdit : ``
        }`}
      </StartDateText>
    </DotLabel>
  );
}

function ScheduledDateChip({
  schedule,
  status,
  lastEditAt,
}: {
  schedule: Schedule | undefined;
  lastEditAt: string | undefined;
  status: ExperienceStatus;
}) {
  return (
    <DotLabel noDot dotColor={getChipColor(status)} textColor="#6b6b6b">
      <StartDateText>
        {`Scheduled ${parseScheduledTime(schedule)} ${
          lastEditAt ? ` (Last edit  ${formatToLocalDate(lastEditAt)})` : ``
        }`}
      </StartDateText>
    </DotLabel>
  );
}

const InfoWrapper = styled.div`
  display: flex;
  width: 100%;
  max-width: 60%;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  flex-direction: column;

  .exp-short-line-wrapper {
    color: black;
  }

  .labels-strip-wrapper .labels-strip-chip {
    border: 1px solid rgba(201, 208, 217, 0.65);
  }
  .labels-strip-wrapper span {
    color: #7b8287;
  }

  ${breakpoints.down(`md`)} {
    max-width: 90%;
    .exp-short-line-wrapper {
      display: none;
    }
  }
`;

const StartDateText = styled.span`
  margin: 1rem 0;
  color: black;
  font-size: 1.2rem;
  font-family: Inter, serif;
  font-weight: 400;
`;
function AudienceHeader({
  description,
  id,
  name,
  onDatePickerClicked,
  isPickerVisible,
}: {
  name: string;
  description: string;
  id: string;
  isPickerVisible: boolean;
  onDatePickerClicked: () => void;
}) {
  const [audience] = useQueryParam(`aud`);
  switch (audience) {
    case `m`:
      name = `Mobile Visitors`;
      description = `Mobile visitors only`;
      break;
    case `d`:
      name = `Desktop Visitors`;
      description = `Desktop visitors only`;
      break;
    case `r`:
      name = `Returning Visitors`;
      description = `Returning visitors only`;
      break;
    case `n`:
      name = `New Visitors`;
      description = `New visitors only`;
      break;
    default:
  }

  return (
    <Controls>
      <BackToLive
        onClick={() => nav(`${Pages.DASHBOARD}?pg=AUDIENCES`)}
      >{`< Back to all audiences`}</BackToLive>
      <VSpace value={1.5} />
      <Heading1 id="details-heading">
        {name}
        <VSpace />
      </Heading1>
      {description ? (
        <DescrWrapper>
          <DescriptionTooltip id={id} description={description} />
          <Descr data-tip data-for={`desc${id}`}>
            {description || ``}
          </Descr>
        </DescrWrapper>
      ) : (
        <VSpace value={0.85} />
      )}
      <VSpace />
      <div style={{ width: `400px`, margin: `0 auto` }}>
        <DatePicker open={isPickerVisible} onClick={onDatePickerClicked} />
      </div>
      <VSpace value={3} />
    </Controls>
  );
}
